import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import { Fish } from '@Types/fish';

export type TicketedEventPurchase = {
  purchase: {
    id: string;
    fishId: string;
    status: string;
    price: number;
    originalPrice: number | null;
    discountAmount: number | null;
    discountType: string | null;
    productDescription: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    confirmationNumber: string | null;
    redemptions?: Array<{
      id: string;
      promoCodeId?: string | null;
      discountType: string;
      discountValue: number;
      discountAmount: number;
      status: string;
      promoCode?: {
        id: string;
        code: string;
        description?: string | null;
      } | null;
      [key: string]: any;
    }>;
    [key: string]: any;
  };
  tickets: Array<{
    id: string;
    ticketTypeId: string;
    ticketType?: {
      id: string;
      name: string;
      price: number;
      [key: string]: any;
    };
    fishId: string;
    fish?: Fish;
    [key: string]: any;
  }>;
};

type TicketedEventPurchasesResponse = TicketedEventPurchase[];
type DefaultError = unknown;

type UseTicketedEventPurchasesParams = {
  eventId?: string;
  /** Override default enabled behavior */
  enabled?: boolean;
};

/**
 * useTicketedEventPurchases
 *
 * Fetches purchases for a ticketed event by fetching tickets with purchases included
 * and aggregating them by purchaseId.
 *
 * @example
 * const purchasesQuery = useTicketedEventPurchases({
 *   eventId: '123',
 * });
 */
export function useTicketedEventPurchases<
  TData = TicketedEventPurchasesResponse,
  TError = DefaultError,
>(
  { eventId, enabled = true }: UseTicketedEventPurchasesParams = {},
  options?: Omit<
    UseQueryOptions<TicketedEventPurchasesResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const queryKey: QueryKey = ['ticketedEventPurchases', eventId];

  return useQuery<TicketedEventPurchasesResponse, TError, TData>({
    queryKey,
    enabled: enabled && !!eventId,
    queryFn: async () => {
      if (!eventId) {
        throw new Error(
          'eventId is required to fetch ticketed event purchases',
        );
      }

      return service.getTicketedEventPurchases(eventId);
    },
    ...options,
  });
}
